import { useState, useEffect, useMemo } from 'react';
import InvoiceDetailView from './InvoiceDetailView';
import BillLoader from './BillLoader';
import { useParams } from 'react-router-dom';
import { getCustomerInvoice } from '../services/invoiceService';

export default function EInvoiceApp() {
  const { id } = useParams(); 
  const [isLoading, setIsLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [logoImage, setLogoImage] = useState(null);
  const [popupMedia, setPopupMedia] = useState(null);
  const [headerSecondaryImage, setHeaderSecondaryImage] = useState(null);
  const [footerSecondaryImage, setFooterSecondaryImage] = useState(null);
  const [currentHeaderSlide, setCurrentHeaderSlide] = useState(0);
  const [currentFooterSlide, setCurrentFooterSlide] = useState(0);
  const [headerTimeRemaining, setHeaderTimeRemaining] = useState(100);
  const [footerTimeRemaining, setFooterTimeRemaining] = useState(100);

  const flattenImageData = (images) => {
    if (!Array.isArray(images)) return [];

    const allImages = [];

    images.forEach((img) => {
      if (img.url) {
        allImages.push({
          url: img.url,
          iteration_time: img.iteration_time || 2,
          redirect_link: img.redirect_link || null, // Include redirect_link from main document
        });
      }

      // Note: extra_images won't have redirect_link as per your requirement
      if (Array.isArray(img.extra_images)) {
        img.extra_images.forEach((extra) => {
          if (extra.url) {
            allImages.push({
              url: extra.url,
              iteration_time: extra.iteration_time || 2,
              redirect_link: null, // Extra images don't have redirect links
            });
          }
        });
      }
    });

    return allImages;
  };

  // Fetch invoice data when component mounts
  useEffect(() => {
    if (id) {
      // console.log("id",id);
      setIsLoading(true);
      getCustomerInvoice(id)
        .then((data) => {
          setInvoice(data);
          const mediaItems = data?.media?.media || [];
          
          // Logo with redirect link
          const logoItem = mediaItems.find((item) => item.key === "logo_primary");
          if (logoItem) {
            setLogoImage({
              url: logoItem.url,
              redirect_link: logoItem.redirect_link || null
            });
          }

          // Popup media (no redirect link as per requirement)
          const popupItem = mediaItems.find((item) => item.key === "popup_media");
          if (popupItem) setPopupMedia(popupItem);

          // Header secondary image with redirect link
          const headerSecondaryItem = mediaItems.find((item) => item.key === "header_image_secondary");
          if (headerSecondaryItem) {
            setHeaderSecondaryImage({
              url: headerSecondaryItem.url,
              redirect_link: headerSecondaryItem.redirect_link || null
            });
          }
          
          // Footer secondary image with redirect link
          const footerSecondaryItem = mediaItems.find((item) => item.key === "footer_image_secondary");
          if (footerSecondaryItem) {
            setFooterSecondaryImage({
              url: footerSecondaryItem.url,
              redirect_link: footerSecondaryItem.redirect_link || null
            });
          }
        })
        .finally(() => {
          setTimeout(() => {
            setIsLoading(false);
          }, 1000);
        });
    }
  }, [id]);

  const headerSlides = useMemo(() => {
    const mediaItems = invoice?.media?.media || [];
    const headerItems = mediaItems.filter((item) => item.key === "header_image");
    return flattenImageData(headerItems);
  }, [invoice]);

  const footerSlides = useMemo(() => {
    const mediaItems = invoice?.media?.media || [];
    const footerItems = mediaItems.filter((item) => item.key === "footer_image");
    return flattenImageData(footerItems);
  }, [invoice]);

  useEffect(() => {
    if (isLoading || headerSlides.length === 0) return;
    
    const currentHeaderSlideData = headerSlides[currentHeaderSlide];
    const duration = currentHeaderSlideData?.iteration_time || 3; 
    
    const updateInterval = 100;
    let progress = 0;
    
    const headerTimer = setInterval(() => {
      const increment = (updateInterval / (duration * 1000)) * 100;
      progress += increment;
      
      if (progress >= 100) {
        setCurrentHeaderSlide((prevSlide) => (prevSlide + 1) % headerSlides.length);
        progress = 0;
      }
      
      setHeaderTimeRemaining(progress);
    }, updateInterval);
    
    return () => clearInterval(headerTimer);
  }, [currentHeaderSlide, isLoading, headerSlides]);
  
  useEffect(() => {
    if (isLoading || footerSlides.length === 0) return;
    
    const currentFooterSlideData = footerSlides[currentFooterSlide];
    const duration = currentFooterSlideData?.iteration_time || 3; 
    
    const updateInterval = 16; 
    let progress = 0;
    
    const footerTimer = setInterval(() => {
      const increment = (updateInterval / (duration * 1000)) * 100;
      progress += increment;
      
      if (progress >= 100) {
        setCurrentFooterSlide((prevSlide) => (prevSlide + 1) % footerSlides.length);
        progress = 0;
      }
      
      setFooterTimeRemaining(progress);
    }, updateInterval);
    
    return () => clearInterval(footerTimer);
  }, [currentFooterSlide, isLoading, footerSlides]);
  
  const nextHeaderSlide = () => {
    setCurrentHeaderSlide((prevSlide) => (prevSlide + 1) % headerSlides.length);
    setHeaderTimeRemaining(100);
  };
  
  const prevHeaderSlide = () => {
    setCurrentHeaderSlide((prevSlide) => (prevSlide - 1 + headerSlides.length) % headerSlides.length);
    setHeaderTimeRemaining(100);
  };
  
  const nextFooterSlide = () => {
    setCurrentFooterSlide((prevSlide) => (prevSlide + 1) % footerSlides.length);
    setFooterTimeRemaining(100);
  };
  
  const prevFooterSlide = () => {
    setCurrentFooterSlide((prevSlide) => (prevSlide - 1 + footerSlides.length) % footerSlides.length);
    setFooterTimeRemaining(100);
  };
  
  return (
    <div className="font-sans bg-gray-100 min-h-screen">
      {isLoading ? (
        <BillLoader />
      ) : (
        <InvoiceDetailView
          setViewingInvoice={() => {}}
          headerSlides={headerSlides}
          footerSlides={footerSlides}
          headerSecondaryImage={headerSecondaryImage}
          footerSecondaryImage={footerSecondaryImage}
          currentHeaderSlide={currentHeaderSlide}
          currentFooterSlide={currentFooterSlide}
          headerTimeRemaining={headerTimeRemaining}
          footerTimeRemaining={footerTimeRemaining}
          nextHeaderSlide={nextHeaderSlide}
          prevHeaderSlide={prevHeaderSlide}
          nextFooterSlide={nextFooterSlide}
          prevFooterSlide={prevFooterSlide}
          invoiceData={invoice}
          logoImage={logoImage}
          popupMedia={popupMedia} 
        />
      )}
    </div>
  );
}